import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import { useActionGuard } from '../../hooks/useActionGuard.ts';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';
import CommerceForm from '../../components/OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceForm.tsx';
import type { CommerceFormSubmitPayload } from '../../components/OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceFormTypes.ts';
import { buildCommercePrefillFromUserData } from '../../utils/commercePrefill.ts';
import { createCommerce } from '../../services/commerceService.ts';
import { extractCommerceIdFromCreateResponse } from '../../services/materialCommerceSheetService.ts';
import { loadUserData } from '../../store/actions';
import { closeMainModal, openMainModal } from '../../store/actions/profileActions';
import { buildProfileUrl, getUserDisplayName } from '../../utils/navigationHelpers.ts';
import { userHasCommerceRegistration } from '../../utils/userCommerces.ts';
import styles from './CommerceAltaPage.module.scss';

const CommerceAltaPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { checkAccess } = useActionGuard();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const userData = useSelector((s: { general?: { userData?: Record<string, unknown> } }) => s.general?.userData);
  const [creatingCommerce, setCreatingCommerce] = useState(false);

  const userId = userData?.id != null ? Number(userData.id) : 0;
  const prefill = useMemo(() => buildCommercePrefillFromUserData(userData), [userData]);
  const flexibleSheetEnabled = userData?.type !== 'JOBBOARD';

  useEffect(() => {
    if (userData == null || !userHasCommerceRegistration(userData)) return;
    navigate('/comercios/lista-precios', { replace: true });
  }, [userData, navigate]);

  useEffect(() => {
    if (!userId) return;
    if (userHasCommerceRegistration(userData)) return;
    if (!checkAccess('register_commerce')) {
      navigate('/dashboard', { replace: true });
    }
  }, [checkAccess, navigate, userData, userId]);

  const handleSubmit = useCallback(
    async (payload: CommerceFormSubmitPayload) => {
      if (creatingCommerce) return;
      setCreatingCommerce(true);
      dispatch(
        openMainModal({
          isLoading: true,
          title: 'Registrando comercio',
          body: 'Estamos guardando los datos de tu comercio…',
          isSuccess: true,
          noActionNeeded: true,
        })
      );
      try {
        const createRes = await createCommerce({
          name: payload.name,
          description: payload.description,
          zone: payload.zone,
          delivery: payload.delivery,
          delivery_condition: payload.delivery_condition,
          url_site_web: payload.url_site_web,
          jurisdiccionId: payload.jurisdiccionId,
          userId: payload.userId,
          logo: payload.logo,
          deliveryScope: payload.deliveryScope,
          deliveryRadiusKm:
            payload.deliveryScope === 'LOCAL' ? payload.deliveryRadiusKm : undefined,
          latitude: payload.deliveryScope === 'LOCAL' ? payload.latitude : undefined,
          longitude: payload.deliveryScope === 'LOCAL' ? payload.longitude : undefined,
          listedZonesByJurisdiction:
            payload.deliveryScope === 'LISTED_ZONES' && payload.listedZonesByJurisdiction?.length
              ? payload.listedZonesByJurisdiction
              : undefined,
          listedGeorefZonaIds:
            payload.deliveryScope === 'LISTED_ZONES' &&
            (!payload.listedZonesByJurisdiction || payload.listedZonesByJurisdiction.length === 0)
              ? payload.listedGeorefZonaIds
              : undefined,
        });
        const commerceId = extractCommerceIdFromCreateResponse(createRes);
        dispatch(loadUserData());
        const nameDisplay = getUserDisplayName(userData);
        if (commerceId != null) {
          const destination = flexibleSheetEnabled
            ? `/comercios/lista-precios?commerceId=${commerceId}&desdeAlta=1`
            : `/comercios/catalogo?commerceId=${commerceId}&desdeAlta=1`;
          if (flexibleSheetEnabled) {
            dispatch(
              openMainModal({
                title: 'Comercio registrado',
                body: 'Tu comercio quedó registrado. Ahora vamos a cargar tu catálogo.',
                iconType: 'success',
                isSuccess: true,
                noLabel: 'Continuar',
                customCloseMainModal: () => {
                  dispatch(closeMainModal());
                  navigate(destination);
                },
              })
            );
          } else {
            dispatch(
              openMainModal({
                title: 'Comercio registrado',
                body: 'Tu comercio quedó registrado. Ahora vamos a gestionar tus materiales.',
                iconType: 'success',
                isSuccess: true,
                noLabel: 'Continuar',
                customCloseMainModal: () => {
                  dispatch(closeMainModal());
                  navigate(destination);
                },
              })
            );
          }
          return;
        }
        dispatch(
          openMainModal({
            title: 'Comercio registrado',
            body: 'Tu comercio quedó registrado correctamente.',
            iconType: 'success',
            isSuccess: true,
            noLabel: 'Ir al perfil',
            customCloseMainModal: () => {
              dispatch(closeMainModal());
              navigate(buildProfileUrl(String(userId), nameDisplay, 'Resumen'));
            },
          })
        );
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'No pudimos registrar el comercio. Intentá de nuevo.';
        dispatch(
          openMainModal({
            title: 'Registro de comercio',
            body: msg,
            iconType: 'error',
            isSuccess: true,
            confirmHandler: null,
          })
        );
      } finally {
        setCreatingCommerce(false);
      }
    },
    [creatingCommerce, dispatch, flexibleSheetEnabled, navigate, userData, userId]
  );

  const handleSkip = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (!userId) {
    return null;
  }

  return (
    <div className={styles.container}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbBar}>
          <Breadcrumbs />
        </div>
        <main className={styles.main}>
          <div className={styles.commerceStack}>
            <div className={styles.commerceHero}>
              <div className={styles.commerceHeroIntro}>
                <h1 className={styles.commerceHeroTitle}>Registrar comercio</h1>
                <div className={styles.sheetIntro}>
                  <p className={styles.subtitle}>
                    Completá los datos del comercio y la zona de alcance; luego vas a poder cargar tus
                    precios y productos de forma manual o con un archivo usando nuestra IA.
                  </p>
                  <ul className={styles.sheetValueList}>
                    <li>Tu comercio queda listo en Edify apenas confirmás estos datos.</li>
                    <li>Definí la zona de alcance de tu comercio y el tipo de entrega que ofreces.</li>
                    <li>Cargá tu catálogo de productos, mayor la cantidad, mayor la posibilidad de que recibas pedidos.</li>
                    <li>Esto nos permite enviarte notificaciones cuando exista un pedido de materiales en tu zona de alcance.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.commerceMain}>
              <CommerceForm
                prefill={prefill}
                userId={userId}
                disabled={creatingCommerce}
                cancelLabel="Cancelar"
                flexibleSheetEnabled={flexibleSheetEnabled}
                suppressCatalogBlocks={true}
                onSkip={handleSkip}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CommerceAltaPage;
